import { FC, useEffect, useMemo, useState } from 'react';
import cx from 'classnames';

// hooks
import { useDashboard } from 'hooks/dashboard';

// types
import { IOrganizationDashboardRequest, IDateRange } from '@joc/api-gateway';

// components
import PendingVolunteersStatistic from './PendingVolunteersStatistic';
import GeneralStatistics, { GeneralStatisticsType } from './GeneralStatistics';
import SmartGroupStatistics, { SmartGroupStatisticsType } from './SmartGroupStatistics';
import StatisticsFilter from './StatisticsFilter';
import Loader from 'shared/components/Loader';

// styles
import styles from './DashboardNew.module.scss';

export type DashboardFiltersType = Omit<IOrganizationDashboardRequest, 'dateRange'> & IDateRange;

const DashboardNew: FC = () => {
	const [isFilterOpen, setIsFilterOpen] = useState(false);
	const [filters, setFilters] = useState<DashboardFiltersType>({
		organizationIds: [],
		fromDate: undefined,
		toDate: undefined,
		createdBy: [],
		opportunityLocationType: [],
	});

	const [pendingVolunteersCount, setPendingVolunteersCount] = useState(0);

	const [generalStatistics, setGeneralStatistics] = useState<GeneralStatisticsType>({
		opportunitiesTotal: 0,
		totalSpendTime: 0,
		volunteersTotal: 0,
	});

	const { data, error, isPending } = useDashboard(filters);

	useEffect(() => {
		if (!isPending && data) {
			setGeneralStatistics({
				opportunitiesTotal: data?.opportunitiesTotal || 0,
				totalSpendTime: data?.totalSpendTime || 0,
				volunteersTotal: data?.volunteersTotal || 0,
			});
			setPendingVolunteersCount(data.claimsCount);
		}
	}, [data, error, isPending]);

	const smartGroupStatistics = useMemo<SmartGroupStatisticsType>(
		() => ({
			opportunitiesInPeriod: data?.opportunitiesInPeriod || 0,
			hoursInPeriod: data?.hoursInPeriod || 0,
			opportunitiesPending: data?.opportunitiesPending || 0,
			opportunitiesUpcoming: data?.opportunitiesUpcoming || 0,
			opportunitiesVacancies: data?.opportunitiesVacancies || 0,
		}),
		[data]
	);

	return (
		<>
			<StatisticsFilter
				isFiltersOpen={isFilterOpen}
				setFiltersOpen={setIsFilterOpen}
				filters={filters}
				setFilters={setFilters}
				isSchool={false}
			/>

			<section className={styles.dashboardNew}>
				<div className={styles.dashboardNew_wrapper}>
					{pendingVolunteersCount > 0 ? (
						<PendingVolunteersStatistic claimCount={pendingVolunteersCount} />
					) : null}

					<GeneralStatistics statistics={generalStatistics} />

					<SmartGroupStatistics
						statistics={smartGroupStatistics}
						setFiltersOpen={setIsFilterOpen}
						filters={filters}
						setFilters={setFilters}
					/>
				</div>

				{isPending ? (
					<Loader
						loadProps={{
							stylePageCenter: true,
							parentClassName: cx(styles.dashboardNew_loader, {
								[styles.dashboardNew_loader_active]: isPending,
							}),
						}}
					/>
				) : null}
			</section>
		</>
	);
};

export default DashboardNew;
